import type { PlatformPhaseId } from './projectPhases';
import { DOMAIN_COLORS } from '../../../theme/kaleidoscope';
import {
  operateTabPath,
  phase0TabPath,
  phase1TabPath,
  projectPhasePath,
} from './projectPhases';

/** Three-phase cognitive + operational lifecycle */
export type ActivityId = 'discover' | 'understand' | 'operate';

export type ActivityReadiness = 'ready' | 'active' | 'available' | 'needs_data';

export interface ProjectActivity {
  id: ActivityId;
  label: string;
  description: string;
  /** Platform phase segment; Operate uses `'3'` (see projectPhasePath). */
  phaseId: PlatformPhaseId | '3';
  defaultPath: (projectId: string) => string;
}

export const PROJECT_ACTIVITIES: ProjectActivity[] = [
  {
    id: 'discover',
    label: 'Discover',
    description: 'Ingest, index, and baseline the repository',
    phaseId: '0',
    defaultPath: (id) => phase0TabPath(id, 'repository'),
  },
  {
    id: 'understand',
    label: 'Understand',
    description: 'Build architecture, graph, and AI reasoning',
    phaseId: '1',
    defaultPath: (id) => phase1TabPath(id, 'insights'),
  },
  {
    id: 'operate',
    label: 'Operate',
    description: 'Incident investigation, autonomous fixes, verification, and learning',
    phaseId: '3',
    defaultPath: (id) => operateTabPath(id, 'incidents'),
  },
];

/**
 * Which lifecycle activity a workspace path belongs to. Project-wide
 * surfaces (governance, enterprise, releases, portfolio, knowledge library,
 * insights) are not part of the loop and return undefined — the rail shows no
 * active node and PhaseContextNav renders no section tabs for them.
 */
export function detectActivityFromPath(pathname: string): ActivityId | undefined {
  if (pathname.includes('/phase-0') || pathname.includes('/ingestion')) return 'discover';
  if (pathname.includes('/phase-1')) return 'understand';
  // Change (phase-2) is now part of Operate
  if (
    pathname.includes('/phase-2') ||
    pathname.includes('/phase-3') ||
    pathname.includes('/operate') ||
    pathname.includes('/ams') ||
    pathname.includes('/modernization')
  ) {
    return 'operate';
  }
  return undefined;
}

/** Why a lifecycle activity is locked — shown wherever a locked node is rendered. */
export const ACTIVITY_LOCK_REASON: Record<ActivityId, string> = {
  discover: 'Discover opens once the project has been created and a repository source is set.',
  understand: 'Understand unlocks after Discover has ingested the repository.',
  operate: 'Operate unlocks after Understand has analyzed the repository.',
};

export const ACTIVITY_EMOJI: Record<ActivityId, string> = {
  discover: '🔭',
  understand: '🎓',
  operate: '⚙️',
};

export const ACTIVITY_COLORS = DOMAIN_COLORS;

export function activityPath(projectId: string, activity: ActivityId): string {
  const def = PROJECT_ACTIVITIES.find((a) => a.id === activity);
  return def ? def.defaultPath(projectId) : projectPhasePath(projectId, '0');
}

export const CYCLE_HUB_NODES: {
  id: string;
  label: string;
  activity: ActivityId;
  detail: string;
}[] = [
  { id: 'repo', label: 'Your repo', activity: 'discover', detail: 'Upload & scan' },
  { id: 'inventory', label: 'Inventory', activity: 'discover', detail: 'Structural deps' },
  { id: 'insights', label: 'Insights', activity: 'understand', detail: 'Workers & reasoning' },
  { id: 'kg', label: 'Graph', activity: 'understand', detail: 'Architecture & KG' },
  { id: 'operate', label: 'Operate', activity: 'operate', detail: 'Investigate · Fix · Verify' },
];
